import { useQuery } from "@tanstack/react-query";
import { api, API_URL, getAccessToken } from "./client";

export interface Summary {
  range: { from: string | null; to: string | null };
  totals: { spend: string; services: number; avgCost: string; days: number };
  previous: { spend: string; changePct: number | null } | null;
  byType: { type: string; total: string; count: number }[];
  byMonth: { month: string; total: string; count: number }[];
  byVehicle: { vehicleId: string; label: string; total: string; count: number }[];
  topItems: { name: string; total: string; count: number }[];
  costPerKm: { value: string; kmCovered: number } | null;
  cadence: { avgDays: number | null; avgKm: number | null };
  expiring: {
    vehicleId: string;
    label: string;
    kind: string;
    date: string;
    daysLeft: number;
  }[];
}

export interface ReportRange {
  vehicleId?: string;
  from?: string;
  to?: string;
  fy?: string;
}

const toQuery = (range: ReportRange) =>
  new URLSearchParams(
    Object.entries(range).filter(([, v]) => v) as [string, string][],
  ).toString();

export function useSummary(range: ReportRange = {}) {
  const qs = toQuery(range);
  return useQuery({
    queryKey: ["summary", range],
    queryFn: () => api<Summary>(`/api/reports/summary${qs ? `?${qs}` : ""}`),
  });
}

/**
 * PDFs need the Authorization header, so a plain link cannot fetch them.
 * Download as a blob and hand the browser an object URL instead.
 */
export async function downloadReport(
  path: string,
  range: ReportRange,
  fallbackName: string,
): Promise<void> {
  const qs = toQuery(range);
  const res = await fetch(`${API_URL}${path}${qs ? `?${qs}` : ""}`, {
    headers: { Authorization: `Bearer ${getAccessToken() ?? ""}` },
  });
  if (!res.ok) throw new Error("Could not generate the report");

  const disposition = res.headers.get("Content-Disposition") ?? "";
  const named = /filename="([^"]+)"/.exec(disposition)?.[1];

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = named ?? fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoked on the next tick so the click has already been handled.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
